import * as React from "react";
import { useState, useContext, useEffect } from "react";
import { Button, Modal, Steps, Form, Result, Space, message } from "antd";

import { useStepsForm } from "sunflower-antd";

import {
  getCompanyMembersApi,
  getMemberPolicyApi,
} from "../../../services/Member/MemberApis";
import { AuthContext } from "../../../context/AuthContext";
import { ClaimContext } from "../../../context/ClaimContext";
import { useGuardedModalClose } from "../../../hooks/useGuardedModalClose";

import FindPolicy from "../../FormInputs/Claim/FindPolicy";
import Checks from "../../FormInputs/Claim/Checks";
import CoveredLive from "../../FormInputs/Claim/CoveredLive";
import Details from "../../FormInputs/Claim/Details";
import Documents from "../../FormInputs/Claim/Documents";

const { Step } = Steps;

interface IModalProps {
  show: boolean;
  showModal: Function;
  handleAdd: Function;
}

const AddClaimModal: React.FC<IModalProps> = ({
  show,
  showModal,
  handleAdd,
}) => {
  const { currentGroup, currentUser } = useContext(AuthContext);
  const { claimData, resetClaimData } = useContext(ClaimContext);

  const [loading, setLoading] = useState(false);
  const [member, setMember] = useState<any>({});
  const [policies, setPolicies] = useState<any>([]);
  const [payments, setPayments] = useState([]);
  const [coveredLives, setCoveredLives] = useState([]);
  const [searchNumber, setSearchNumber] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const activeGroup = (currentUser?.Group ?? []).find(
    (g: any) => String(g.id) === String(currentGroup)
  );
  const claimType: "Internal" | "External" = activeGroup?.IsExternal ? "External" : "Internal";

  const onChange = (e: any) => {
    setSearchNumber(e.target.value);
  };

  const onSearch = async () => {
    try {
      const { data } = await getCompanyMembersApi(currentGroup);

      if (Array.isArray(data)) {
        const needle = (searchNumber || "").trim().toLowerCase();
        const filteredData = needle
          ? data.filter(
              (member) =>
                (member.Policy?.PolicyNumber &&
                  member.Policy.PolicyNumber.toLowerCase().includes(needle)) ||
                (member.IdNumber &&
                  member.IdNumber.toLowerCase().includes(needle))
            )
          : [];
        setPolicies(filteredData);
      } else {
        message.info("No data or invalid data format for Policy");
      }
    } catch (error) {
      console.error("Error fetching company members:", error);
      message.error("Failed to fetch company members. Please try again later.");
    }
  };

  const fetchPayments = async (num?: string) => {
    if (num) {
      const { data, status } = await getMemberPolicyApi(num);

      if (status === 200) {
        setMember(data);
        setPayments(data.Policy.Transaction);
        setCoveredLives(data.CoveredLive);
        // Both flows enter Checks next. External skips Covered Live later.
        gotoStep(current + 1);
      }
    } else {
      message.error("Policy Number or ID Number is required");
    }
  };

  const {
    form,
    current,
    gotoStep,
    stepsProps,
    formProps,
    submit,
    formLoading,
  } = useStepsForm({
    isBackValidate: false,
    async submit(values: any) {
      setLoading(true);
      const { id: DeceasedId, ...restClaimData } = claimData;

      // Normalise DateOfDeath: backend wants YYYY-MM-DD, not a dayjs/ISO timestamp.
      const dod =
        values?.DateOfDeath && typeof values.DateOfDeath?.format === "function"
          ? values.DateOfDeath.format("YYYY-MM-DD")
          : values?.DateOfDeath;

      let payload: any;
      if (claimType === "External") {
        // External endpoint has a strict shape; only forward the fields it expects.
        payload = {
          DeceasedName: values.DeceasedName,
          DeceasedSurname: values.DeceasedSurname,
          DeceasedIdNumber: values.DeceasedIdNumber,
          DeceasedType: values.DeceasedType ?? "Main",
          BenName: values.BenName,
          BenSurname: values.BenSurname,
          BenIdNumber: values.BenIdNumber,
          DateOfDeath: dod,
          CauseOfDeath: values.CauseOfDeath,
          ClaimAmount: values.ClaimAmount != null ? String(values.ClaimAmount) : "",
          AdditionalNotes: values.Notes ?? "",
          AccountNumber: values.AccountNumber,
          AccountType: values.AccountType ?? "Savings",
          Bank: values.Bank,
          BranchCode: values.BranchCode,
          Notes: values.Notes ?? "",
          Documents: restClaimData.Documents ?? [],
          ClaimType: "External",
        };
      } else {
        payload = { ...values, DateOfDeath: dod, ...restClaimData, DeceasedId, ClaimType: "Internal" };
      }

      console.log("[AddClaim] claimType:", claimType, "payload keys:", Object.keys(payload), "Documents count:", payload.Documents?.length);
      const response = await handleAdd(payload);

      if (response.success) {
        resetClaimData();
        setLoading(false);
        return true;
      }
      setErrorMsg(response.message);
      setLoading(false);
      return false;
    },
    total: 11, // Reduced from 12 to 11
  });

  const resetModal = () => {
    setPolicies([]);
    showModal();
    gotoStep(0);
    form.resetFields();
  };

  const { requestClose, onValuesChange, markModalSaved } = useGuardedModalClose({
    id: "add-claim-modal",
    open: show,
    hasChanges: current > 0 && current < 8,
    onClose: resetModal,
  });

  const handleModal = () => {
    void requestClose();
  };

  const handleCompletedClose = () => {
    markModalSaved();
    resetModal();
  };

  const formList = [
    <FindPolicy
      gotoStep={gotoStep}
      current={current}
      onChange={onChange}
      onSearch={onSearch}
      fetchPayments={fetchPayments}
      policies={policies}
      claimType={claimType}
    />,

    <Checks
      gotoStep={gotoStep}
      current={current}
      payments={payments}
      member={member}
      claimType={claimType}
    />,

    <CoveredLive
      gotoStep={gotoStep}
      current={current}
      coveredLives={coveredLives}
    />,

    <Details gotoStep={gotoStep} current={current} claimType={claimType} />,

    <Documents
      gotoStep={gotoStep}
      current={current}
      submit={submit}
      loading={loading}
    />,
  ];

  return (
    <Modal
      title="New Claim"
      open={show}
      onOk={handleModal}
      onCancel={handleModal}
      width={1050}
      footer={null}
    >
      {/* Steps Form */}
      <div style={{ display: "flex", gap: 24 }}>
        {current !== 10 && (
          <div style={{ flexShrink: 0, borderRight: "1px solid #f0f0f0", paddingRight: 16, whiteSpace: "nowrap" }}>
            {claimType === "External" ? (
              (() => {
                // External: Find Policy (0) → Checks (1) → Details (3) → Documents (4).
                // Covered Live (2) is skipped.
                const visibleIndexes = [0, 1, 3, 4];
                const sidebarCurrent = Math.max(0, visibleIndexes.indexOf(current));
                return (
                  <Steps
                    direction="vertical"
                    current={sidebarCurrent === -1 ? 0 : sidebarCurrent}
                    onChange={(idx) => gotoStep(visibleIndexes[idx])}
                  >
                    <Step title="Find Policy" />
                    <Step title="Checks" />
                    <Step title="Details" />
                    <Step title="Documents" />
                  </Steps>
                );
              })()
            ) : (
              <Steps {...stepsProps} direction="vertical">
                <Step title="Find Policy" />
                <Step title="Checks" />
                <Step title="Covered Live" />
                <Step title="Details" />
                <Step title="Documents" />
              </Steps>
            )}
          </div>
        )}

        <div style={{ flex: 1, minWidth: 0, marginTop: 24, overflowX: "auto" }}>
          <Form {...formProps} style={{ width: "100%" }} onValuesChange={onValuesChange}>
            {formList[current]}
          </Form>

          {current === 8 && (
            <Result
              status="success"
              title="Successfully submitted."
              subTitle="The system administrator will be notified for approval."
            >
              <Space direction="vertical" style={{ width: "100%" }}>
                <Button
                  loading={loading}
                  block
                  size="large"
                  onClick={handleCompletedClose}
                >
                  Okay
                </Button>
              </Space>
            </Result>
          )}

          {current === 9 && (
            <Result status="error" title={errorMsg}>
              <Space direction="vertical" style={{ width: "100%" }}>
                <Button block size="large" onClick={handleCompletedClose}>
                  Okay
                </Button>
              </Space>
            </Result>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default AddClaimModal;
